import type { BaseAccountResource, AccountResource } from "./accountResource";
import type { PaginatedCollection } from "./paginatedCollection";
import type { SensitiveValue } from "./propertyValueResource";
import type { ResourceCollection } from "./resourceCollection";

export interface PlatformHubAccountResource extends BaseAccountResource {}

export interface UsernamePasswordPlatformHubAccountResource extends PlatformHubAccountResource {
    Username: string;
    Password: SensitiveValue;
}

export interface SshKeyPairPlatformHubAccountResource extends PlatformHubAccountResource {
    Username: string;
    PrivateKeyFile: SensitiveValue;
    PrivateKeyPassphrase: SensitiveValue;
}

export interface TokenPlatformHubAccountResource extends PlatformHubAccountResource {
    Token: SensitiveValue;
}

export interface AzureServicePrincipalPlatformHubAccountResource extends PlatformHubAccountResource {
    SubscriptionNumber: string;
    ClientId: string;
    TenantId: string;
    Password: SensitiveValue;
    AzureEnvironment: string;
    ResourceManagementEndpointBaseUri: string;
    ActiveDirectoryEndpointBaseUri: string;
}

export interface AzureSubscriptionPlatformHubAccountResource extends PlatformHubAccountResource {
    SubscriptionNumber: string;
    CertificateBytes: SensitiveValue;
    CertificateThumbprint: string;
    AzureEnvironment: string;
    ServiceManagementEndpointBaseUri: string;
    ServiceManagementEndpointSuffix: string;
}

export interface AzureOidcAccountPlatformHubResource extends PlatformHubAccountResource {
    SubscriptionNumber: string;
    ClientId: string;
    TenantId: string;
    AzureEnvironment: string;
    TenantedSubjectGeneration: boolean;
    ResourceManagementEndpointBaseUri: string;
    ActiveDirectoryEndpointBaseUri: string;
    DeploymentSubjectKeys: string[];
    HealthCheckSubjectKeys: string[];
    AccountTestSubjectKeys: string[];
    Audience: string;
}

export interface AmazonWebServicesAccessKeyPlatformHubAccountResource extends PlatformHubAccountResource {
    AccessKey: string;
    SecretKey: SensitiveValue;
}

export interface AmazonWebServicesOidcPlatformHubAccountResource extends PlatformHubAccountResource {
    RoleArn: string;
    SessionDuration: string;
    DeploymentSubjectKeys: string[];
    HealthCheckSubjectKeys: string[];
    AccountTestSubjectKeys: string[];
}

export interface GoogleCloudPlatformHubAccountResource extends PlatformHubAccountResource {
    JsonKey: SensitiveValue;
}

export interface GenericOidcPlatformHubAccountResource extends PlatformHubAccountResource {
    TenantedSubjectGeneration: boolean;
    DeploymentSubjectKeys: string[];
    HealthCheckSubjectKeys: string[];
    AccountTestSubjectKeys: string[];
    Audience: string;
}

/**
 * Type guard to check if an account is a PlatformHub account.
 * PlatformHub accounts don't have EnvironmentIds, unlike regular AccountResources.
 * @param account
 */
export function isPlatformHubAccount(account: AccountResource | PlatformHubAccountResource): account is PlatformHubAccountResource {
    return !("EnvironmentIds" in account);
}

/**
 * Type guard to check if a collection of accounts is a ResourceCollection of AccountResource.
 * This helps distinguish between regular account collections and PlatformHub account collections.
 * Warning, this only checks the first account
 * @param accounts
 */
export function isAccountResourceCollection(accounts: ResourceCollection<AccountResource> | ResourceCollection<PlatformHubAccountResource> | PaginatedCollection<PlatformHubAccountResource>): accounts is ResourceCollection<AccountResource> {
    return accounts.Items.length === 0 || !isPlatformHubAccount(accounts.Items[0]);
}
